import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { SWRConfiguration } from 'swr';
import { CustomFieldOptionType } from '../../components/CustomField/CustomField.type';
import { DEDUP_INTERVAL_AUTOCOMPLETE } from '../../constants/swr-config';
import { SimpleUser, customSWRResponse } from '../../model/types';
import { fetcherWithToken } from '../../utils/fetchers';
import { getMaskedEmailForUI, localizedSearchTermForApi, nonNullishValue } from '../../utils/helpers';
import useSWRCacheKeys from '../useSWRCacheKeys';
import { AutocompleteResponseType } from './useAutocompleteSuggestions';

const generateLabel: (userFriendlyValue: string) => string = (userFriendlyValue: string) => {
    return !getMaskedEmailForUI(userFriendlyValue) ? 'Not a valid option' : getMaskedEmailForUI(userFriendlyValue);
};

/**
 * NOTE: This function has the same rules as the backend to generate a user-friendly value from a simple user object.
 *
 * The function generates a user-friendly value from a simple user object by combining their full name,
 * email, and phone number.
 * @param {SimpleUser} user - The `user` parameter is of type `SimpleUser`.
 * @returns The function `generateUserFriendlyValueFromSimpleUser` returns a user-friendly value based
 * on the properties of the `SimpleUser` object. If the `fullName` property is not present or empty, it
 * checks for the presence of the `email` and `phone` properties. If either of them is present, it
 * returns the corresponding value. If none of them are present, it returns an empty
 */
export const generateUserFriendlyValueFromSimpleUser = (user: SimpleUser) => {
    if (!user.fullName) {
        if (user.email) {
            return user.email;
        }

        if (user.phone) {
            return user.phone;
        }
        return '';
    } else {
        if (user.email) {
            return user.fullName + ' (' + user.email + ')';
        }

        if (user.phone) {
            return user.fullName + ' (' + user.phone + ')';
        }
        return user.fullName;
    }
};

export enum AutoCompleteUserKeyMap {
    user = 'ts.requester',
    agent = 'team-user',
    followers = 'ts.followers',
    emailCc = 'email_cc',
    mention = 'mention'
}
export default function useAutocompleteUserSearch(
    searchTerm: string,
    autoCompleteUserKey: AutoCompleteUserKeyMap,
    config: SWRConfiguration = {}
): customSWRResponse<(Omit<CustomFieldOptionType, 'order'> & { disabled?: boolean })[]> {
    const keys = useSWRCacheKeys(
        `/field-values/${autoCompleteUserKey}?searchTerm=${localizedSearchTermForApi(searchTerm)}`
    );
    const { t } = useTranslation('common');

    const isAbleToMakeRequest = nonNullishValue(searchTerm) && searchTerm?.length > 0 && nonNullishValue(keys);
    const { data, error, isLoading } = useSWR<AutocompleteResponseType<SimpleUser>>(
        isAbleToMakeRequest ? keys : null,
        fetcherWithToken,
        { dedupingInterval: DEDUP_INTERVAL_AUTOCOMPLETE, ...config }
    );

    const searchResultAsOptions = useMemo(
        () =>
            data?.result?.map((user) => {
                const generatedLabel = generateUserFriendlyValueFromSimpleUser(user);
                return {
                    label: generatedLabel,
                    value: `${user.id}|${generatedLabel}`,
                    disabled: !getMaskedEmailForUI(user.email)
                };
            }),
        [data]
    );
    const translatedError = useMemo(() => {
        if (error) {
            if (error?.response?.data?.message?.includes('Search term must be at least'))
                return { message: t('searchTermTooShort') } as Error;
            else return error;
        } else return undefined;
    }, [error, t]);

    return {
        data: searchResultAsOptions,
        error: translatedError,
        isLoading
    };
}

export { generateLabel };
